import { useCallback, useEffect, useRef, useState } from "react";

const VELOCIDADE_DIGITACAO_MS = 45;

// Vivia dentro de `IntroMascote.jsx` e saiu de lá quando a cerimônia de card
// passou a digitar a fala do mascote também (03/08/2026): um hook não pertence a
// uma tela, e importar a tela inteira só para pegar o hook amarraria a cerimônia
// à abertura sem nenhuma razão.
export function prefereMenosMovimento() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Efeito de máquina de escrever: revela um caractere por vez, como se o mascote
 * estivesse falando. Pula direto para o texto inteiro sob prefers-reduced-motion.
 *
 * A digitação recomeça quando `texto` muda, e é assim que quem monta o hook antes
 * de ter o texto (passando `""`) consegue disparar a fala na hora certa.
 *
 * @returns {{digitado: string, digitando: boolean, completar: Function}}
 *   `completar` existe para o toque em dois estágios da cerimônia: o primeiro toque
 *   termina a fala, o segundo é que fecha. As pílulas têm 92 a 190 caracteres — de
 *   4,1s a 8,5s de digitação —, e sem uma saída assim quem tem pressa perde o texto
 *   ou espera olhando.
 */
export function useTextoDigitado(texto = "") {
  const [tamanhoVisivel, setTamanhoVisivel] = useState(() =>
    prefereMenosMovimento() ? texto.length : 0
  );
  // O id do intervalo numa ref, e não numa variável do efeito, porque `completar`
  // precisa PARÁ-LO: sem isso o intervalo seguiria contando de onde estava e
  // reescreveria um texto menor no tique seguinte — o texto completaria e voltaria
  // a apagar, o que é pior que não ter o atalho.
  const intervalo = useRef(null);

  useEffect(() => {
    if (prefereMenosMovimento()) {
      setTamanhoVisivel(texto.length);
      return undefined;
    }
    // Zera junto com o `indice`: sem isto, trocar de texto começaria a nova fala
    // com o tamanho já revelado da anterior — o primeiro quadro apareceria com um
    // pedaço pronto, e a digitação pareceria ter perdido o começo.
    setTamanhoVisivel(0);
    let indice = 0;
    intervalo.current = setInterval(() => {
      indice += 1;
      setTamanhoVisivel(indice);
      if (indice >= texto.length) clearInterval(intervalo.current);
    }, VELOCIDADE_DIGITACAO_MS);
    return () => clearInterval(intervalo.current);
  }, [texto]);

  const completar = useCallback(() => {
    clearInterval(intervalo.current);
    setTamanhoVisivel(texto.length);
  }, [texto]);

  return {
    digitado: texto.slice(0, tamanhoVisivel),
    digitando: tamanhoVisivel < texto.length,
    completar
  };
}

export default useTextoDigitado;
